/**
 * Cumpli design-system primitives (Tailwind). Restyled to the product look
 * (warm canvas, "Cumpli green", Fraunces/Hanken/Plex). Export signatures are
 * unchanged so existing screens upgrade without edits; a few primitives
 * (Pill, Avatar, PageHeader) were added for the Phase-1 screens.
 */
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Check, AlertCircle, X } from 'lucide-react';
import type { EstadoExpediente } from '../lib/phase1';
import { usePrefersReducedMotion } from '../lib/motion';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

type ButtonVariant = 'default' | 'outline' | 'destructive' | 'ghost';

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }
>(({ className, variant = 'default', ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-40',
      variant === 'default' &&
        'bg-primary text-primary-foreground shadow-[0_6px_14px_-6px_rgba(138,46,60,.6)] hover:bg-primary-dark',
      variant === 'outline' &&
        'border border-line-strong bg-panel text-ink hover:border-ink-faint',
      variant === 'destructive' && 'bg-tone-red text-white hover:bg-tone-red-dark',
      variant === 'ghost' && 'text-ink-soft hover:bg-canvas',
      className
    )}
    {...props}
  />
));
Button.displayName = 'Button';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'flex h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50',
        className
      )}
      {...props}
    />
  )
);
Input.displayName = 'Input';

export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-soft"
    >
      {children}
    </label>
  );
}

export function FieldError({ message }: { message?: string | undefined }) {
  if (!message) return null;
  return <p className="mt-1 text-sm text-tone-red">{message}</p>;
}

export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('rounded-2xl border border-line bg-panel p-6 shadow-card', className)} {...rest}>
      {children}
    </div>
  );
}

export function Dialog({
  open,
  title,
  children,
  onClose,
  size = 'md',
  wide,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  size?: 'md' | 'lg';
  /** Wider panel for content-heavy dialogs (forms with previews). */
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'max-h-[calc(100vh-2rem)] w-full overflow-y-auto rounded-2xl bg-panel p-6 shadow-pop',
          size === 'lg' ? 'max-w-3xl' : wide ? 'max-w-2xl' : 'max-w-md'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 font-serif text-xl font-medium">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * Wide right-side slide-over drawer — for content-heavy detail (a full activity
 * entry + its before→after diff) that would cramp `Dialog`'s centered card.
 * Full-width on mobile (the panel is `w-full max-w-xl`, so it fills any
 * viewport narrower than the cap), ESC + overlay-click to close, and moves
 * focus into the panel on open so Escape/Tab start from inside it.
 */
export function SlideOver({
  open,
  title,
  subtitle,
  children,
  onClose,
  widthClassName = 'max-w-xl',
}: {
  open: boolean;
  title: string;
  subtitle?: string | undefined;
  children: ReactNode;
  onClose: () => void;
  /** Panel width cap — defaults to `max-w-xl`; widen (e.g. `max-w-2xl`) for
   *  content-heavy usages like a full read-only aggregate snapshot. */
  widthClassName?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'absolute inset-y-0 right-0 flex w-full flex-col overflow-hidden bg-panel shadow-pop outline-none',
          widthClassName
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-line bg-panel px-6 py-5">
          <div className="min-w-0">
            <h2 className="font-serif text-xl font-medium text-ink">{title}</h2>
            {subtitle && <p className="mt-1 truncate text-sm text-ink-soft">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink-soft hover:bg-canvas"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto overflow-x-clip px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

/** Auto-dismiss window (ms) for a toast, and the fade-out lead-in before it goes. */
const TOAST_MS = 3000;
const TOAST_EXIT_MS = 260;

/**
 * A single compact, single-line maroon toast. Slides up on enter, drains a 3s
 * underline, then fades out. Owns its own lifecycle: after ~3s it calls
 * `onDone` so the provider can drop it. Under reduced motion it appears/leaves
 * with opacity only (no slide) and shows a static underline — still auto-dismisses.
 */
export function ToastItem({
  message,
  kind,
  onDone,
}: {
  message: string;
  kind: 'success' | 'error';
  onDone: () => void;
}) {
  const reduced = usePrefersReducedMotion();
  const [exiting, setExiting] = useState(false);
  const [drain, setDrain] = useState(false);
  // Keep the latest onDone without re-arming the timers each render.
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    // Kick the underline drain on the next frame so the CSS transition runs.
    const raf = requestAnimationFrame(() => setDrain(true));
    const exitTimer = setTimeout(() => setExiting(true), TOAST_MS - TOAST_EXIT_MS);
    const doneTimer = setTimeout(() => doneRef.current(), TOAST_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, []);

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto relative w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line-strong bg-panel shadow-pop transition-all duration-200',
        !reduced && 'motion-safe:animate-slide-up',
        exiting ? 'translate-y-1 opacity-0' : 'opacity-100'
      )}
    >
      <div className="flex items-center gap-2.5 px-4 py-3">
        <span
          className={cn(
            'grid h-5 w-5 shrink-0 place-items-center rounded-full',
            kind === 'success' ? 'bg-primary/10 text-primary-dark' : 'bg-tone-red-bg text-tone-red'
          )}
        >
          {kind === 'success' ? (
            <Check className="h-3 w-3" strokeWidth={3} />
          ) : (
            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
          )}
        </span>
        <span className="truncate text-sm font-semibold text-ink">{message}</span>
      </div>
      {/* Draining underline — 3s width→0 for success, static brick for errors. */}
      <div
        className={cn(
          'absolute bottom-0 left-0 h-0.5',
          kind === 'success' ? 'bg-primary/60' : 'bg-tone-red/60',
          !reduced && 'transition-[width] ease-linear'
        )}
        style={{
          width: reduced ? '100%' : drain ? '0%' : '100%',
          transitionDuration: reduced ? undefined : `${TOAST_MS}ms`,
        }}
      />
    </div>
  );
}

export function Badge({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        muted ? 'bg-tone-slate-bg text-tone-slate' : 'bg-primary/10 text-primary-dark'
      )}
    >
      {children}
    </span>
  );
}

// ---------- added primitives ----------

export type Tone = 'green' | 'amber' | 'red' | 'blue' | 'slate';

// Muted, warm, maroon-harmonized status palette (subtle by design).
const TONE: Record<Tone, string> = {
  green: 'bg-primary/10 text-primary-dark', // positive / "Listo" — brand maroon
  amber: 'bg-tone-amber-bg text-tone-amber', // attention — soft clay
  red: 'bg-tone-red-bg text-tone-red',       // alert — muted brick
  blue: 'bg-tone-blue-bg text-tone-blue',    // in progress — dusty mauve
  slate: 'bg-tone-slate-bg text-tone-slate', // neutral — warm taupe
};

/** Status pill with a leading dot — used across the compliance screens. */
export function Pill({
  children,
  tone = 'slate',
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[11px] font-medium',
        TONE[tone],
        className
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

export function Avatar({ initials, className }: { initials: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-grid h-7 w-7 place-items-center rounded-full bg-tone-slate-bg text-xs font-bold text-tone-slate',
        className
      )}
    >
      {initials}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-line', className)} />;
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="grid place-items-center gap-2 px-6 py-16 text-center">
      {icon && <div className="text-ink-faint">{icon}</div>}
      <div className="font-serif text-lg">{title}</div>
      {hint && <p className="max-w-sm text-sm text-ink-soft">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-serif text-3xl font-medium tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** Thin progress fill (0..1) — used by flows and the top bar. */
export function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-line">
      <div
        className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Lightweight hover tooltip for jargon. Wraps any inline element. */
export function Tooltip({ content, children }: { content: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex cursor-default items-center">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute -top-10 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-lg bg-side px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-pop transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {content}
        <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-side" />
      </span>
    </span>
  );
}

/** GOV.UK layer: internal estado → plain words + tone for the secretary. */
export function friendlyEstado(estado: EstadoExpediente): { label: string; tone: Tone } {
  switch (estado) {
    case 'Nuevo':
      return { label: 'Recién creado', tone: 'slate' };
    case 'En Revisión':
      return { label: 'En revisión', tone: 'blue' };
    case 'Docs Pendientes':
      return { label: 'Falta información', tone: 'amber' };
    case 'Escalado':
      return { label: 'Revisión especial', tone: 'red' };
    case 'Aprobado':
    case 'Activo':
      return { label: 'Listo', tone: 'green' };
    case 'Rechazado':
      return { label: 'No aprobado', tone: 'red' };
    case 'Actualización Requerida':
      return { label: 'Necesita actualización', tone: 'amber' };
    default:
      return { label: estado, tone: 'slate' };
  }
}
